import axios from 'axios';

/**
 * Admin Withdrawal Service
 * Handles all admin withdrawal-related API operations
 * Uses the global axios interceptors from AdminAuthService for authentication
 */
class AdminWithdrawalService {
  /**
   * Get withdrawal queue for admin review
   * @param {object} params - Query parameters
   * @returns {Promise<object>} Withdrawal queue
   */
  async getWithdrawalQueue(params = {}) {
    try {
      const response = await axios.get('/api/withdrawals/admin/queue', { params });
      return response.data;
    } catch (error) {
      console.error('Get withdrawal queue error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get withdrawal details for admin review
   * @param {string} withdrawalId - Withdrawal ID
   * @returns {Promise<object>} Withdrawal details
   */
  async getWithdrawalDetails(withdrawalId) {
    try {
      const response = await axios.get(`/api/withdrawals/admin/${withdrawalId}`);
      return response.data;
    } catch (error) {
      console.error('Get withdrawal details error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Approve a withdrawal
   * @param {string} withdrawalId - Withdrawal ID
   * @param {number} approvedAmount - Approved amount (optional)
   * @returns {Promise<object>} Approval result
   */
  async approveWithdrawal(withdrawalId, approvedAmount = null) {
    try {
      const response = await axios.put(`/api/withdrawals/admin/${withdrawalId}/approve`, {
        approvedAmount
      });
      return response.data;
    } catch (error) {
      console.error('Approve withdrawal error:', error);
      throw this.handleError(error);
    }
  }



  /**
   * Reject a withdrawal with required reason
   * @param {string} withdrawalId - Withdrawal ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<object>} Rejection result
   */
  async rejectWithdrawal(withdrawalId, reason) {
    try {
      const response = await axios.put(`/api/withdrawals/admin/${withdrawalId}/reject`, { reason });
      return response.data;
    } catch (error) {
      console.error('Reject withdrawal error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get withdrawal activity logs
   * @param {object} params - Query parameters
   * @returns {Promise<object>} Activity logs
   */
  async getWithdrawalLogs(params = {}) {
    try {
      const response = await axios.get('/api/withdrawals/admin/logs', { params });
      return response.data;
    } catch (error) {
      console.error('Get withdrawal logs error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get withdrawal statistics for dashboard
   * @returns {Promise<object>} Withdrawal statistics
   */
  async getWithdrawalStats() {
    try {
      const response = await axios.get('/api/withdrawals/admin/stats');
      return response.data;
    } catch (error) {
      console.error('Get withdrawal stats error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Process withdrawal queue (trigger processing)
   * @returns {Promise<object>} Processing result
   */
  async processWithdrawalQueue() {
    try {
      const response = await axios.post('/api/withdrawals/process-queue');
      return response.data;
    } catch (error) {
      console.error('Process withdrawal queue error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Check for incomplete payments
   * @returns {Promise<object>} Incomplete payments
   */
  async checkIncompletePayments() {
    try {
      const response = await axios.get('/api/withdrawals/check-incomplete');
      return response.data;
    } catch (error) {
      console.error('Check incomplete payments error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { data } = error.response;
      return {
        message: data.error?.message || 'An error occurred',
        code: data.error?.code || 'API_ERROR',
        status: error.response.status,
        response: error.response,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        status: 0,
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        status: 0,
      };
    }
  }

  /**
   * Format withdrawal status for display
   * @param {string} status - Withdrawal status
   * @param {string} currency - Currency code
   * @returns {object} Formatted status
   */
  formatStatus(status, currency = 'PI') {
    const isNGN = currency === 'NGN';

    const statusMap = {
      pending: {
        label: 'Pending Review',
        color: 'yellow',
        description: 'Waiting for admin approval',
        bgClass: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      },
      approved: {
        label: 'Approved',
        color: 'blue',
        description: isNGN ? 'Approved for bank transfer' : 'Approved and queued for processing',
        bgClass: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      processing: {
        label: 'Processing',
        color: 'blue',
        description: isNGN ? 'Processing bank transfer' : 'Being processed on Pi blockchain',
        bgClass: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      submitted: {
        label: 'Submitted',
        color: 'blue',
        description: isNGN ? 'Submitted to bank' : 'Submitted to Pi blockchain',
        bgClass: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      completed: {
        label: 'Completed',
        color: 'green',
        description: 'Successfully completed',
        bgClass: 'bg-green-100 text-green-800 border-green-200'
      },
      rejected: {
        label: 'Rejected',
        color: 'red',
        description: 'Rejected by admin',
        bgClass: 'bg-red-100 text-red-800 border-red-200'
      },
      failed: {
        label: 'Failed',
        color: 'red',
        description: 'Processing failed',
        bgClass: 'bg-red-100 text-red-800 border-red-200'
      }
    };

    return statusMap[status] || {
      label: status,
      color: 'gray',
      description: 'Unknown status',
      bgClass: 'bg-gray-100 text-gray-800 border-gray-200'
    };
  }

  /**
   * Format currency amount for display
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code (PI or NGN)
   * @returns {string} Formatted amount
   */
  formatAmount(amount, currency = 'PI') {
    const symbol = currency === 'NGN' ? '₦' : 'π';
    if (!amount) return `${symbol} 0`;

    const numAmount = parseFloat(amount);

    // Format with appropriate decimal places
    if (numAmount >= 1000000) {
      return `${symbol} ${(numAmount / 1000000).toFixed(1)}M`;
    } else if (numAmount >= 1000) {
      return `${symbol} ${(numAmount / 1000).toFixed(1)}K`;
    } else if (numAmount % 1 === 0) {
      return `${symbol} ${numAmount.toLocaleString()}`;
    } else {
      return `${symbol} ${numAmount.toFixed(2)}`;
    }
  }

  /**
   * Get priority level based on amount and age
   * @param {object} withdrawal - Withdrawal object
   * @returns {object} Priority info
   */
  getPriority(withdrawal) {
    const amount = parseFloat(withdrawal.requested_amount);
    const hoursOld = (new Date() - new Date(withdrawal.requested_at)) / (1000 * 60 * 60);

    if (amount >= 50000 || hoursOld >= 72) {
      return {
        level: 'high',
        label: 'High Priority',
        class: 'bg-red-100 text-red-800 border-red-200',
        icon: 'alert-triangle'
      };
    } else if (amount >= 20000 || hoursOld >= 24) {
      return {
        level: 'medium',
        label: 'Medium Priority',
        class: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: 'clock'
      };
    } else {
      return {
        level: 'low',
        label: 'Low Priority',
        class: 'bg-green-100 text-green-800 border-green-200',
        icon: 'check-circle'
      };
    }
  }

  /**
   * Validate withdrawal approval
   * @param {object} withdrawal - Withdrawal object
   * @param {number} approvedAmount - Approved amount
   * @returns {object} Validation result
   */
  validateApproval(withdrawal, approvedAmount) {
    if (!approvedAmount) {
      approvedAmount = withdrawal.requested_amount;
    }

    const numAmount = parseFloat(approvedAmount);

    if (isNaN(numAmount)) {
      return { valid: false, error: 'Invalid approved amount' };
    }

    if (numAmount <= 0) {
      return { valid: false, error: 'Approved amount must be greater than 0' };
    }

    if (numAmount > parseFloat(withdrawal.requested_amount)) {
      return { valid: false, error: 'Approved amount cannot exceed requested amount' };
    }

    return { valid: true };
  }

  /**
   * Validate rejection reason
   */
  validateRejectionReason(reason) {
    if (!reason || reason.trim().length < 10) {
      throw new Error('Rejection reason must be at least 10 characters long');
    }
    if (reason.trim().length > 1000) {
      throw new Error('Rejection reason must be less than 1000 characters');
    }
    return reason.trim();
  }

  /**
   * Get time ago string for dates
   */
  getTimeAgo(date) {
    const now = new Date();
    const diffInSeconds = Math.floor((now - new Date(date)) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return new Date(date).toLocaleDateString();
  }

  /**
   * Format withdrawal data for display
   */
  formatWithdrawalForDisplay(withdrawal) {
    return {
      ...withdrawal,
      formattedRequestedAmount: this.formatAmount(withdrawal.requested_amount, withdrawal.currency),
      formattedApprovedAmount: withdrawal.approved_amount
        ? this.formatAmount(withdrawal.approved_amount, withdrawal.currency)
        : null,
      requestedDate: new Date(withdrawal.requested_at).toLocaleDateString(),
      requestedTimeAgo: this.getTimeAgo(withdrawal.requested_at),
      approvedDate: withdrawal.approved_at
        ? new Date(withdrawal.approved_at).toLocaleDateString()
        : null,
      completedDate: withdrawal.completed_at
        ? new Date(withdrawal.completed_at).toLocaleDateString()
        : null,
      ownerName: withdrawal.owner?.full_name || withdrawal.owner?.username || 'Unknown',
      projectTitle: withdrawal.project?.title || 'Unknown Project',
      statusInfo: this.formatStatus(withdrawal.status, withdrawal.currency),
      priorityInfo: this.getPriority(withdrawal),
      shortWallet: withdrawal.currency === 'PI' && withdrawal.recipient_wallet
        ? `${withdrawal.recipient_wallet.slice(0, 8)}...${withdrawal.recipient_wallet.slice(-6)}`
        : withdrawal.currency === 'NGN'
          ? `${withdrawal.bank_name} (${withdrawal.account_number})`
          : 'N/A'
    };
  }
}

export default new AdminWithdrawalService();
